/**
 * Aircraft List Page
 * Displays all aircraft in a data grid showing registration, type, name,
 * serviceability status, total hours, location, and upcoming bookings.
 * Click opens the aircraft detail modal.
 */

import { getAircraft, getBookingFlights, getAircraftById, getLastFuelOil } from '../services/api.js';
import { createDataGrid } from '../components/data-grid.js';
import { getRole, getUser } from '../services/auth.js';
import { createFuelOilGauges } from '../components/fuel-oil-gauges.js';

/**
 * Return the CSS class token for a given serviceability status.
 * @param {string} status - 'SERVICEABLE' or 'UNSERVICEABLE'
 * @returns {'serviceable'|'unserviceable'}
 */
export function getServiceabilityClass(status) {
  return status === 'UNSERVICEABLE' ? 'unserviceable' : 'serviceable';
}

/**
 * Transform a raw aircraft record into the shape expected by the grid.
 * @param {Object} aircraft - Raw aircraft row from the database
 * @returns {Object} Grid-ready row
 */
export function formatAircraftForGrid(aircraft) {
  return {
    id: aircraft.id,
    registration: aircraft.registration || '',
    type: aircraft.type || '',
    name: aircraft.name || '',
    status: aircraft.status || 'SERVICEABLE',
    location: aircraft.location || '',
    total_hours: aircraft.total_hours != null ? Number(aircraft.total_hours) : 0,
  };
}

/**
 * Build a Set of aircraft IDs that have at least one booking
 * with a flight_date in the next `days` calendar days (inclusive of today).
 * @param {Object[]} bookings
 * @param {number} [days=7]
 * @returns {Set<string>}
 */
export function getAircraftIdsWithUpcomingBookings(bookings, days = 7) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cutoff = new Date(today);
  cutoff.setDate(cutoff.getDate() + days);
  const todayStr = today.toISOString().split('T')[0];
  const cutoffStr = cutoff.toISOString().split('T')[0];

  const ids = new Set();
  for (const b of bookings) {
    if (b.flight_date >= todayStr && b.flight_date <= cutoffStr && b.aircraft_id) {
      ids.add(b.aircraft_id);
    }
  }
  return ids;
}

// ---------------------------------------------------------------------------
// Aircraft Summary Modal (lightweight — no sub-queries)
// ---------------------------------------------------------------------------

/** @type {HTMLElement|null} */
let _activeSummaryModal = null;

function _closeSummaryModal() {
  if (!_activeSummaryModal) return;
  _activeSummaryModal.remove();
  _activeSummaryModal = null;
  document.removeEventListener('keydown', _handleSummaryKey);
}

function _handleSummaryKey(e) {
  if (e.key === 'Escape') { e.preventDefault(); _closeSummaryModal(); }
}

/**
 * Build a mini calendar element showing booked days for the next 30 days.
 * @param {Object[]} bookings - Booking records with flight_date
 * @returns {HTMLElement}
 */
export function createMiniBookingCalendar(bookings) {
  const container = document.createElement('div');
  container.className = 'mini-calendar';

  const title = document.createElement('h4');
  title.className = 'mini-calendar__title';
  title.textContent = 'Bookings — Next 30 Days';
  container.appendChild(title);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Build set of booked date strings
  const bookedDates = new Set();
  for (const b of bookings) {
    if (b.flight_date) bookedDates.add(b.flight_date);
  }

  // Weekday header
  const weekdayHeader = document.createElement('div');
  weekdayHeader.className = 'mini-calendar__weekdays';
  for (const d of ['M', 'T', 'W', 'T', 'F', 'S', 'S']) {
    const span = document.createElement('span');
    span.textContent = d;
    weekdayHeader.appendChild(span);
  }
  container.appendChild(weekdayHeader);

  const grid = document.createElement('div');
  grid.className = 'mini-calendar__grid';

  // Leading empty cells to align first day to correct weekday
  const startDow = (today.getDay() + 6) % 7; // Mon=0
  for (let i = 0; i < startDow; i++) {
    const empty = document.createElement('div');
    empty.className = 'mini-calendar__day mini-calendar__day--empty';
    grid.appendChild(empty);
  }

  // 30 day cells
  for (let i = 0; i < 30; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    const dateStr = d.toISOString().split('T')[0];
    const dayNum = d.getDate();

    const cell = document.createElement('div');
    cell.className = 'mini-calendar__day';
    if (i === 0) cell.classList.add('mini-calendar__day--today');
    if (bookedDates.has(dateStr)) cell.classList.add('mini-calendar__day--booked');
    cell.textContent = dayNum;
    cell.title = dateStr;
    grid.appendChild(cell);
  }

  container.appendChild(grid);
  return container;
}

/**
 * Open a lightweight summary modal for the given aircraft row.
 * Fetches bookings and fuel/oil data asynchronously.
 * @param {Object} row - Grid row data (from formatAircraftForGrid)
 */
export function openAircraftSummaryModal(row) {
  if (_activeSummaryModal) _closeSummaryModal();

  const backdrop = document.createElement('div');
  backdrop.className = 'detail-modal__backdrop';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-label', row.registration);

  const modal = document.createElement('div');
  modal.className = 'detail-modal';

  // Header with X button
  const header = document.createElement('div');
  header.className = 'detail-modal__header';
  header.style.display = 'flex';
  header.style.alignItems = 'center';
  header.style.justifyContent = 'space-between';

  const titleEl = document.createElement('h2');
  titleEl.className = 'detail-modal__title';
  titleEl.textContent = `${row.registration}${row.name ? ' — ' + row.name : ''}`;
  header.appendChild(titleEl);

  const closeX = document.createElement('button');
  closeX.type = 'button';
  closeX.className = 'detail-modal__close-x';
  closeX.setAttribute('aria-label', 'Close');
  closeX.textContent = '✕';
  closeX.addEventListener('click', _closeSummaryModal);
  header.appendChild(closeX);

  modal.appendChild(header);

  // Body — summary fields + async sections
  const body = document.createElement('div');
  body.className = 'detail-modal__body';

  const statusCls = getServiceabilityClass(row.status);
  body.innerHTML = `
    <div class="aircraft-summary">
      <div class="aircraft-summary__field">
        <span class="aircraft-summary__label">Registration</span>
        <span class="aircraft-summary__value">${row.registration}</span>
      </div>
      <div class="aircraft-summary__field">
        <span class="aircraft-summary__label">Type</span>
        <span class="aircraft-summary__value">${row.type}</span>
      </div>
      <div class="aircraft-summary__field">
        <span class="aircraft-summary__label">Name</span>
        <span class="aircraft-summary__value">${row.name || '—'}</span>
      </div>
      <div class="aircraft-summary__field">
        <span class="aircraft-summary__label">Status</span>
        <span class="aircraft-summary__value"><span class="badge badge--${statusCls}">${row.status}</span></span>
      </div>
      <div class="aircraft-summary__field">
        <span class="aircraft-summary__label">Total Hours</span>
        <span class="aircraft-summary__value">${Number(row.total_hours).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
      </div>
      <div class="aircraft-summary__field">
        <span class="aircraft-summary__label">Location</span>
        <span class="aircraft-summary__value">${row.location || '—'}</span>
      </div>
    </div>
  `;

  // Placeholder containers for async-loaded sections
  const calendarSlot = document.createElement('div');
  calendarSlot.className = 'aircraft-summary__section';
  calendarSlot.innerHTML = '<p class="text-muted">Loading bookings…</p>';
  body.appendChild(calendarSlot);

  const fuelSlot = document.createElement('div');
  fuelSlot.className = 'aircraft-summary__section';
  fuelSlot.innerHTML = '<p class="text-muted">Loading fuel & oil…</p>';
  body.appendChild(fuelSlot);

  modal.appendChild(body);

  // Footer — Close button
  const footer = document.createElement('div');
  footer.className = 'detail-modal__footer';
  const spacer = document.createElement('div');
  spacer.className = 'detail-modal__footer-spacer';
  footer.appendChild(spacer);
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'btn btn--primary';
  closeBtn.textContent = 'Close';
  closeBtn.addEventListener('click', _closeSummaryModal);
  footer.appendChild(closeBtn);
  modal.appendChild(footer);

  backdrop.appendChild(modal);
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) _closeSummaryModal(); });

  _activeSummaryModal = backdrop;
  document.body.appendChild(backdrop);
  document.addEventListener('keydown', _handleSummaryKey);
  closeBtn.focus();

  // Async: load bookings calendar + fuel/oil in parallel
  const loadAsync = async () => {
    try {
      const [bookings, aircraft, fuelOil] = await Promise.all([
        getBookingFlights({ aircraft_id: row.id }).catch(() => []),
        getAircraftById(row.id).catch(() => null),
        getLastFuelOil(row.id).catch(() => null),
      ]);

      // Mini calendar
      if (!_activeSummaryModal) return; // modal was closed
      calendarSlot.innerHTML = '';
      calendarSlot.appendChild(createMiniBookingCalendar(bookings));

      // Fuel & oil gauges
      fuelSlot.innerHTML = '';
      if (aircraft) {
        fuelSlot.appendChild(createFuelOilGauges({
          aircraft,
          fuelOilRecord: fuelOil,
          lastFlightDate: null,
        }));
      } else {
        fuelSlot.innerHTML = '<p class="text-muted">Aircraft data unavailable.</p>';
      }
    } catch {
      if (!_activeSummaryModal) return;
      calendarSlot.innerHTML = '<p class="text-muted">Could not load bookings.</p>';
      fuelSlot.innerHTML = '<p class="text-muted">Could not load fuel & oil data.</p>';
    }
  };
  loadAsync();
}

// ---------------------------------------------------------------------------
// Main render
// ---------------------------------------------------------------------------

/**
 * Render the aircraft list page.
 */
export async function renderAircraftList() {
  const app = document.getElementById('app');

  const role = getRole();
  const canAdd = role === 'admin' || role === 'owner';
  const isOwnerRole = role === 'owner';

  app.innerHTML = `
    <div class="container">
      <h2>Aircraft</h2>
      <div id="aircraft-list-container"><p>Loading aircraft…</p></div>
    </div>
  `;

  try {
    const [aircraft, bookings] = await Promise.all([
      getAircraft(),
      getBookingFlights().catch(() => []),
    ]);

    const container = document.getElementById('aircraft-list-container');
    if (!container) return;

    if (!aircraft || aircraft.length === 0) {
      container.innerHTML = '<p>No aircraft found.</p>';
      return;
    }

    const upcomingIds = getAircraftIdsWithUpcomingBookings(bookings);
    const allRows = aircraft.map(formatAircraftForGrid);

    // Persistent "My Aircraft" checkbox (survives grid rebuilds)
    let myAircraftCheckbox = null;
    if (isOwnerRole) {
      myAircraftCheckbox = document.createElement('input');
      myAircraftCheckbox.type = 'checkbox';
      myAircraftCheckbox.id = 'my-aircraft-filter';
    }

    function buildGrid(rows) {
      container.innerHTML = '';

      const grid = createDataGrid({
        columns: [
          { key: 'registration', label: 'Rego', className: 'col--rego' },
          { key: 'type', label: 'Type' },
          { key: 'name', label: 'Name' },
          { key: 'location', label: 'Location' },
          {
            key: 'status',
            label: 'Status',
            html: true,
            format: (value) => {
              const cls = getServiceabilityClass(value);
              return `<span class="badge badge--${cls}">${value}</span>`;
            },
          },
          {
            key: 'total_hours',
            label: 'Total Hrs',
            className: 'col--right',
            format: (value) => Number(value).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          },
          {
            key: 'id',
            label: 'Booked (7d)',
            sortable: false,
            className: 'col--center',
            format: (id) => (upcomingIds.has(id) ? '📅' : '—'),
          },
        ],
        data: rows,
        defaultSort: 'registration',
        emptyMessage: 'No aircraft found.',
        onRowClick: (row) => openAircraftSummaryModal(row),
        actions: canAdd
          ? [{ label: 'Edit', className: 'data-grid__action-btn--edit', onClick: (row) => {
              // TODO: open edit form for this aircraft
              console.log('Edit aircraft', row.id);
            }}]
          : null,
      });

      // Inject toolbar: filter is already first child; insert My Aircraft + Add Aircraft on same line
      const filterInput = grid.querySelector('.data-grid__filter');
      if (filterInput) {
        // Wrap filter in a toolbar row
        const toolbar = document.createElement('div');
        toolbar.className = 'data-grid__toolbar';

        // Left side: filter + My Aircraft
        const left = document.createElement('div');
        left.className = 'data-grid__toolbar-left';
        filterInput.parentNode.insertBefore(toolbar, filterInput);
        left.appendChild(filterInput);

        if (myAircraftCheckbox) {
          const label = document.createElement('label');
          label.className = 'aircraft-list__filter-toggle';
          label.appendChild(myAircraftCheckbox);
          label.appendChild(document.createTextNode(' My Aircraft'));
          left.appendChild(label);
        }
        toolbar.appendChild(left);

        // Right side: Add Aircraft button
        if (canAdd) {
          const addBtn = document.createElement('button');
          addBtn.className = 'btn btn--primary';
          addBtn.textContent = '+ Add Aircraft';
          addBtn.addEventListener('click', () => {
            // TODO: open add-aircraft form / modal
            console.log('Add Aircraft clicked');
          });
          toolbar.appendChild(addBtn);
        }

        // Remove the default add button if data-grid rendered one
        const defaultAddBtn = grid.querySelector('.data-grid__add-btn');
        if (defaultAddBtn) defaultAddBtn.remove();
      }

      container.appendChild(grid);
    }

    buildGrid(allRows);

    // "My Aircraft" filter toggle for owners
    if (myAircraftCheckbox) {
      const userId = getUser()?.id;
      myAircraftCheckbox.addEventListener('change', () => {
        if (myAircraftCheckbox.checked && userId) {
          const filtered = allRows.filter((r) => {
            const orig = aircraft.find((a) => a.id === r.id);
            return orig?.meta?.owner_id === userId;
          });
          buildGrid(filtered);
        } else {
          buildGrid(allRows);
        }
      });
    }
  } catch (err) {
    const container = document.getElementById('aircraft-list-container');
    if (container) {
      container.innerHTML = '<p class="text-danger">Failed to load aircraft.</p>';
    }
    console.error('Failed to load aircraft:', err);
  }
}
